// @ts-nocheck
import { classifyBubbleScores } from './scanLogic';

function luminance(r,g,b){ return (r*.299+g*.587+b*.114)/255; }

function diskDarkness(ctx,x,y,r,factor=.56){
  const sx=Math.max(0,Math.floor(x-r)), sy=Math.max(0,Math.floor(y-r));
  const w=Math.max(1,Math.floor(r*2)), h=w;
  const d=ctx.getImageData(sx,sy,w,h).data;
  let dark=0,total=0;
  for(let yy=0;yy<h;yy++)for(let xx=0;xx<w;xx++){
    const dx=xx-r,dy=yy-r;
    if(dx*dx+dy*dy>r*r*factor) continue;
    const i=(yy*w+xx)*4;
    dark += 1-luminance(d[i],d[i+1],d[i+2]);
    total++;
  }
  return total?dark/total:0;
}

// v15.8.11 — leitura multizona da bolha. O centro privilegia X/riscos/lápis,
// enquanto a zona média ajuda no preenchimento parcial. A borda impressa recebe
// peso baixo para não ser confundida com marcação.
function bubbleMarkScore(ctx,x,y,r){
  const core=diskDarkness(ctx,x,y,r*.62,.72);
  const middle=diskDarkness(ctx,x,y,r*.94,.58);
  const tiny=diskDarkness(ctx,x,y,r*.38,.82);
  return Math.max(tiny*.72+core*.28, core*.76+middle*.24, core);
}

function markerCandidates(ctx,w,h){
  const step=Math.max(2,Math.round(Math.min(w,h)/520));
  const gw=Math.max(1,Math.floor(w/step)), gh=Math.max(1,Math.floor(h/step));
  const img=ctx.getImageData(0,0,w,h).data;
  const mask=new Uint8Array(gw*gh), seen=new Uint8Array(gw*gh);
  for(let gy=0;gy<gh;gy++)for(let gx=0;gx<gw;gx++){
    const x=Math.min(w-1,gx*step),y=Math.min(h-1,gy*step),i=(y*w+x)*4;
    if(luminance(img[i],img[i+1],img[i+2])<.28) mask[gy*gw+gx]=1;
  }
  const minSide=Math.max(4,Math.min(w,h)*.012), maxSide=Math.min(w,h)*.11;
  const out=[];
  for(let i=0;i<mask.length;i++){
    if(!mask[i]||seen[i]) continue;
    const q=[i];seen[i]=1;
    let n=0,sx=0,sy=0,minx=1e9,maxx=-1,miny=1e9,maxy=-1;
    while(q.length){
      const z=q.pop(), gx=z%gw, gy=Math.floor(z/gw);
      n++;sx+=gx;sy+=gy;
      minx=Math.min(minx,gx);maxx=Math.max(maxx,gx);miny=Math.min(miny,gy);maxy=Math.max(maxy,gy);
      for(const [dx,dy] of [[1,0],[-1,0],[0,1],[0,-1]]){
        const nx=gx+dx,ny=gy+dy;
        if(nx>=0&&ny>=0&&nx<gw&&ny<gh){
          const ni=ny*gw+nx;
          if(mask[ni]&&!seen[ni]){seen[ni]=1;q.push(ni)}
        }
      }
    }
    const bw=(maxx-minx+1)*step,bh=(maxy-miny+1)*step;
    if(bw<minSide||bh<minSide||bw>maxSide||bh>maxSide) continue;
    const squareness=Math.min(bw,bh)/Math.max(bw,bh);
    const cells=(maxx-minx+1)*(maxy-miny+1);
    const fill=n/Math.max(1,cells);
    if(squareness<.62||fill<.34) continue;
    out.push({
      x:(sx/n)*step,y:(sy/n)*step,
      bw,bh,n,fill,squareness,
      markerScore:(bw*bh)*fill*squareness
    });
  }
  return out.sort((a,b)=>b.markerScore-a.markerScore).slice(0,18);
}

function orderQuad(points){
  const pts=points.map(p=>({...p}));
  const sum=p=>p.x+p.y, diff=p=>p.x-p.y;
  const tl=pts.reduce((a,b)=>sum(a)<sum(b)?a:b);
  const br=pts.reduce((a,b)=>sum(a)>sum(b)?a:b);
  const tr=pts.reduce((a,b)=>diff(a)>diff(b)?a:b);
  const bl=pts.reduce((a,b)=>diff(a)<diff(b)?a:b);
  if(new Set([tl,tr,bl,br]).size<4) return null;
  return {tl,tr,bl,br};
}
function dist(a,b){return Math.hypot(a.x-b.x,a.y-b.y)}
function polygonArea(c){
  const p=[c.tl,c.tr,c.br,c.bl];
  let s=0;
  for(let i=0;i<4;i++){const a=p[i],b=p[(i+1)%4];s+=a.x*b.y-b.x*a.y}
  return Math.abs(s)/2;
}
function quadMetrics(c,w,h){
  const tw=dist(c.tl,c.tr),bw=dist(c.bl,c.br),lh=dist(c.tl,c.bl),rh=dist(c.tr,c.br);
  const fw=(tw+bw)/2,fh=(lh+rh)/2;
  const ratio=fw/Math.max(1,fh), canonical=Math.max(ratio,1/Math.max(.001,ratio));
  const aspectScore=Math.max(0,1-Math.abs(canonical-1.79)/.72);
  const opposite=(Math.min(tw,bw)/Math.max(tw,bw)+Math.min(lh,rh)/Math.max(lh,rh))/2;
  const coverage=polygonArea(c)/(w*h);
  const diag1=dist(c.tl,c.br),diag2=dist(c.tr,c.bl);
  const diagScore=Math.min(diag1,diag2)/Math.max(diag1,diag2);
  const geometry=Math.max(0,Math.min(1,opposite*.62+diagScore*.38));
  return {fw,fh,ratio,canonical,aspectScore,geometry,coverage};
}

export function findAnswerFrameCorners(ctx,w,h){
  const candidates=markerCandidates(ctx,w,h);
  if(candidates.length<4) return null;
  let best=null;
  const max=Math.min(14,candidates.length);
  for(let a=0;a<max-3;a++)for(let b=a+1;b<max-2;b++)for(let c=b+1;c<max-1;c++)for(let d=c+1;d<max;d++){
    const pts=[candidates[a],candidates[b],candidates[c],candidates[d]];
    const ordered=orderQuad(pts); if(!ordered) continue;
    const m=quadMetrics(ordered,w,h);
    if(m.coverage<.055||m.geometry<.52||m.aspectScore<.25) continue;
    const sizes=pts.map(p=>Math.sqrt(p.bw*p.bh));
    const sizeConsistency=Math.min(...sizes)/Math.max(...sizes);
    if(sizeConsistency<.42) continue;
    const markerQuality=pts.reduce((s,p)=>s+p.fill*p.squareness,0)/4;
    const score=m.coverage*2.5+m.geometry*.95+m.aspectScore*1.15+sizeConsistency*.55+markerQuality*.35;
    if(!best||score>best.score) best={corners:ordered,score,...m,sizeConsistency,markerQuality,candidates};
  }
  return best;
}


// v15.8 — detector de enquadramento ao vivo baseado no fluxo de câmera v15.1.
// Ele serve SOMENTE para estabilizar/capturar a imagem. A leitura final continua
// usando detectAnswerFrame/analyzeBubbleSheet com a geometria exata do gabarito.
function liveLargestDarkComponent(ctx,rx,ry,rw,rh){
  const step=3,gw=Math.max(1,Math.floor(rw/step)),gh=Math.max(1,Math.floor(rh/step));
  const img=ctx.getImageData(rx,ry,rw,rh).data,mask=new Uint8Array(gw*gh),seen=new Uint8Array(gw*gh);
  for(let gy=0;gy<gh;gy++)for(let gx=0;gx<gw;gx++){
    const x=Math.min(rw-1,gx*step),y=Math.min(rh-1,gy*step),i=(y*rw+x)*4;
    if(luminance(img[i],img[i+1],img[i+2])<.24)mask[gy*gw+gx]=1;
  }
  let best=null;
  for(let i=0;i<mask.length;i++){
    if(!mask[i]||seen[i])continue;
    const q=[i];seen[i]=1;let n=0,sx=0,sy=0,minx=1e9,maxx=0,miny=1e9,maxy=0;
    while(q.length){const z=q.pop(),gx=z%gw,gy=Math.floor(z/gw);n++;sx+=gx;sy+=gy;minx=Math.min(minx,gx);maxx=Math.max(maxx,gx);miny=Math.min(miny,gy);maxy=Math.max(maxy,gy);
      for(const [dx,dy] of [[1,0],[-1,0],[0,1],[0,-1]]){const nx=gx+dx,ny=gy+dy;if(nx>=0&&ny>=0&&nx<gw&&ny<gh){const ni=ny*gw+nx;if(mask[ni]&&!seen[ni]){seen[ni]=1;q.push(ni)}}}
    }
    const ww=maxx-minx+1,hh=maxy-miny+1,aspect=Math.min(ww,hh)/Math.max(ww,hh),score=n*aspect;
    if(n>10&&(!best||score>best.score))best={score,x:rx+(sx/n)*step,y:ry+(sy/n)*step,n,aspect};
  }
  return best;
}

// v15.8.9 — detector ESTRITO exclusivo para decidir a captura automática.
// A leitura final das respostas continua usando findAnswerFrameCorners(), mais tolerante.
function liveMarkerCornerScore(ctx,p,w,h){
  // Quadrado sólido permanece escuro perto dos 4 cantos da própria caixa.
  // Uma bolha circular pode ter fill alto, mas clareia nesses mesmos pontos.
  let dark=0,n=0;
  for(const sx of [-.38,.38])for(const sy of [-.38,.38]){
    const cx=Math.max(1,Math.min(w-2,Math.round(p.x+p.bw*sx)));
    const cy=Math.max(1,Math.min(h-2,Math.round(p.y+p.bh*sy)));
    const d=ctx.getImageData(cx-1,cy-1,3,3).data;
    for(let i=0;i<d.length;i+=4){dark+=1-luminance(d[i],d[i+1],d[i+2]);n++}
  }
  return n?dark/n:0;
}
function liveMarkerCandidatesStrict(ctx,w,h){
  const base=markerCandidates(ctx,w,h),minDim=Math.min(w,h),out=[];
  for(const p of base){
    const side=Math.sqrt(p.bw*p.bh);
    if(p.squareness<.76||p.fill<.54||side<minDim*.011||side>minDim*.075)continue;
    const cornerScore=liveMarkerCornerScore(ctx,p,w,h);
    if(cornerScore<.48)continue;
    out.push({...p,cornerScore});
  }
  return out.slice(0,14);
}
function liveRightAngle(a,b,c){
  const ux=a.x-b.x,uy=a.y-b.y,vx=c.x-b.x,vy=c.y-b.y;
  const den=Math.max(1e-6,Math.hypot(ux,uy)*Math.hypot(vx,vy));
  return Math.max(0,1-Math.abs((ux*vx+uy*vy)/den));
}
function liveStrictMetrics(c,w,h){
  const tw=dist(c.tl,c.tr),bw=dist(c.bl,c.br),lh=dist(c.tl,c.bl),rh=dist(c.tr,c.br);
  const fw=(tw+bw)/2,fh=(lh+rh)/2,canonical=Math.max(fw/Math.max(1,fh),fh/Math.max(1,fw));
  const aspectScore=Math.max(0,1-Math.abs(canonical-1.79)/.52);
  const opposite=(Math.min(tw,bw)/Math.max(tw,bw)+Math.min(lh,rh)/Math.max(lh,rh))/2;
  const diag1=dist(c.tl,c.br),diag2=dist(c.tr,c.bl),diagScore=Math.min(diag1,diag2)/Math.max(diag1,diag2);
  const angleScore=(liveRightAngle(c.tr,c.tl,c.bl)+liveRightAngle(c.tl,c.tr,c.br)+liveRightAngle(c.tl,c.bl,c.br)+liveRightAngle(c.tr,c.br,c.bl))/4;
  const coverage=polygonArea(c)/(w*h);
  const geometry=Math.max(0,Math.min(1,opposite*.38+diagScore*.22+angleScore*.40));
  return {fw,fh,canonical,aspectScore,opposite,diagScore,angleScore,coverage,geometry};
}
function findLiveAnswerFrameCornersStrict(ctx,w,h){
  const candidates=liveMarkerCandidatesStrict(ctx,w,h);
  if(candidates.length<4)return null;
  let best=null;const max=Math.min(12,candidates.length);
  for(let a=0;a<max-3;a++)for(let b=a+1;b<max-2;b++)for(let c=b+1;c<max-1;c++)for(let d=c+1;d<max;d++){
    const pts=[candidates[a],candidates[b],candidates[c],candidates[d]],ordered=orderQuad(pts);if(!ordered)continue;
    const m=liveStrictMetrics(ordered,w,h);
    if(m.coverage<.075||m.geometry<.70||m.angleScore<.76||m.opposite<.68||m.aspectScore<.44)continue;
    const sizes=pts.map(p=>Math.sqrt(p.bw*p.bh)),sizeConsistency=Math.min(...sizes)/Math.max(...sizes);
    if(sizeConsistency<.64)continue;
    const markerQuality=pts.reduce((sum,p)=>sum+p.fill*p.squareness*p.cornerScore,0)/4;
    if(markerQuality<.34)continue;
    const cornerConsistency=Math.min(...pts.map(p=>p.cornerScore));
    if(cornerConsistency<.48)continue;
    const score=m.coverage*2.1+m.geometry*1.15+m.aspectScore*1.25+sizeConsistency*.78+markerQuality*.70;
    if(!best||score>best.score)best={corners:ordered,score,...m,sizeConsistency,markerQuality,candidates};
  }
  return best;
}

export function detectLiveSheetFrame(canvas){
  const ctx=canvas.getContext('2d',{willReadFrequently:true}),w=canvas.width,h=canvas.height;

  // v15.8.9 — captura automática usa EXCLUSIVAMENTE os quatro marcadores reais.
  // O antigo fallback por regiões podia confundir bolhas, letras e outros componentes
  // escuros do gabarito com os marcadores de canto.
  const exact=findLiveAnswerFrameCornersStrict(ctx,w,h);
  if(!exact){
    return {ok:false,corners:null,width:w,height:h,quality:0,geometry:0,coverage:0,mode:'strict-markers'};
  }
  const quality=Math.max(0,Math.min(1,
    exact.geometry*.34+exact.aspectScore*.24+exact.sizeConsistency*.20+
    exact.markerQuality*.12+Math.min(1,exact.coverage/.34)*.10
  ));
  const ok=quality>=.62&&exact.geometry>=.72&&exact.angleScore>=.78&&
    exact.sizeConsistency>=.68&&exact.coverage>=.075;
  return {
    ok,corners:exact.corners,quality,geometry:exact.geometry,coverage:exact.coverage,
    angleScore:exact.angleScore,sizeConsistency:exact.sizeConsistency,markerQuality:exact.markerQuality,
    width:w,height:h,mode:'strict-markers'
  };
}


export function detectAnswerFrame(canvas){
  const ctx=canvas.getContext('2d',{willReadFrequently:true}),w=canvas.width,h=canvas.height;
  const found=findAnswerFrameCorners(ctx,w,h);
  if(!found) return {ok:false,corners:null,width:w,height:h,quality:0,geometry:0,coverage:0};
  const quality=Math.max(0,Math.min(1,
    found.geometry*.40+found.aspectScore*.30+found.sizeConsistency*.15+
    Math.min(1,found.coverage/.42)*.15
  ));
  return {
    ok:true,corners:found.corners,width:w,height:h,
    quality,geometry:found.geometry,coverage:found.coverage,
    frameWidth:found.fw,frameHeight:found.fh,ratio:found.ratio
  };
}

// Compatibilidade com testes/versões anteriores.
export const detectSheetFrame=detectAnswerFrame;

export function rotateCanvas(source,deg){
  if(typeof document==='undefined'||!document.createElement) return null;
  if(deg%360===0) return source;
  const swap=Math.abs(deg)%180===90,c=document.createElement('canvas');
  c.width=swap?source.height:source.width;c.height=swap?source.width:source.height;
  const ctx=c.getContext('2d');
  ctx.translate(c.width/2,c.height/2);ctx.rotate(deg*Math.PI/180);
  ctx.drawImage(source,-source.width/2,-source.height/2);
  return c;
}

export function normalizeAnswerOrientation(canvas){
  if(typeof document==='undefined'){
    return {canvas,rotation:0,frame:detectAnswerFrame(canvas)};
  }
  const variants=[];
  for(const rotation of [0,90,270,180]){
    const c=rotateCanvas(canvas,rotation); if(!c) continue;
    const frame=detectAnswerFrame(c); if(!frame.ok) continue;
    // Depois da normalização, o quadro do gabarito deve estar em paisagem.
    const landscape=frame.frameWidth>=frame.frameHeight;
    const canonicalRatio=frame.frameWidth/Math.max(1,frame.frameHeight);
    const ratioScore=Math.max(0,1-Math.abs(canonicalRatio-1.79)/.55);
    const score=frame.quality*1.7+frame.coverage*.8+ratioScore*.8+(landscape?.35:-.55);
    variants.push({canvas:c,rotation,frame,score});
  }
  if(!variants.length) return {canvas,rotation:0,frame:detectAnswerFrame(canvas)};
  variants.sort((a,b)=>b.score-a.score);
  return variants[0];
}
export const normalizeSheetOrientation=normalizeAnswerOrientation;

// v15.8.22 — mapeamento determinístico da posição do QR para a rotação necessária.
// Canvas usa eixo Y para baixo e rotações positivas no sentido horário.
export function rotationFromQrVector(dx,dy){
  if(dx<0&&dy<0)return 0;    // QR já está acima/esquerda
  if(dx>0&&dy<0)return 270;  // acima/direita -> gira anti-horário 90°
  if(dx>0&&dy>0)return 180;  // abaixo/direita -> de cabeça para baixo
  if(dx<0&&dy>0)return 90;   // abaixo/esquerda -> gira horário 90°
  return null;
}

export function detectBestAnswerFrame(canvas){
  const n=normalizeAnswerOrientation(canvas);
  return {...n.frame,rotation:n.rotation,normalizedCanvas:n.canvas};
}
export const detectBestSheetFrame=detectBestAnswerFrame;

export function videoFrameToCanvas(video,maxWidth=1280){
  const sourceW=video.videoWidth||video.clientWidth||1280,sourceH=video.videoHeight||video.clientHeight||720;
  const scale=Math.min(1,maxWidth/sourceW),canvas=document.createElement('canvas');
  canvas.width=Math.max(1,Math.round(sourceW*scale));canvas.height=Math.max(1,Math.round(sourceH*scale));
  canvas.getContext('2d').drawImage(video,0,0,canvas.width,canvas.height);
  return canvas;
}

// v15.8.15 — localização física da bolha impressa perto da posição esperada.
// Em vez de medir um ponto fixo, procura o anel circular real e mede somente o interior.
function sampleDarkness(ctx,x,y,half=1){
  const sx=Math.max(0,Math.round(x-half)), sy=Math.max(0,Math.round(y-half));
  const w=Math.max(1,Math.round(half*2+1)), h=w;
  const d=ctx.getImageData(sx,sy,w,h).data;
  let sum=0,n=0;
  for(let i=0;i<d.length;i+=4){sum+=1-luminance(d[i],d[i+1],d[i+2]);n++;}
  return n?sum/n:0;
}
function ringDarkness(ctx,x,y,r,samples=28){
  let sum=0;
  const half=Math.max(1,Math.round(r*.10));
  for(let i=0;i<samples;i++){
    const a=(i/samples)*Math.PI*2;
    sum+=sampleDarkness(ctx,x+Math.cos(a)*r,y+Math.sin(a)*r,half);
  }
  return sum/samples;
}
function darknessAtImage(img,w,h,x,y){
  const xx=Math.max(0,Math.min(w-1,Math.round(x))),yy=Math.max(0,Math.min(h-1,Math.round(y)));
  const i=(yy*w+xx)*4;return 1-luminance(img[i],img[i+1],img[i+2]);
}
function ringDarknessImage(img,w,h,x,y,r,samples=16){
  let sum=0;for(let i=0;i<samples;i++){const a=(i/samples)*Math.PI*2;sum+=darknessAtImage(img,w,h,x+Math.cos(a)*r,y+Math.sin(a)*r)}return sum/samples;
}
function centerDarknessImage(img,w,h,x,y,r){
  let sum=0,n=0;const step=Math.max(1,Math.round(r*.45));
  for(let dy=-r;dy<=r;dy+=step)for(let dx=-r;dx<=r;dx+=step){if(dx*dx+dy*dy>r*r)continue;sum+=darknessAtImage(img,w,h,x+dx,y+dy);n++}return n?sum/n:0;
}
function locatePrintedBubbleFast(img,w,h,expectedX,expectedY,expectedR){
  // v15.8.22: toda a fotografia já está em memória. Evita milhares de getImageData(),
  // principal causa da demora observada no Android. Com orientação pelo QR confirmada,
  // a busca pode ser menor sem perder tolerância a perspectiva/impressão.
  const search=Math.max(3,expectedR*.82),step=Math.max(1,Math.round(expectedR*.34));
  let best=null;
  for(const rf of [.90,1.02,1.14]){
    const r=expectedR*rf;
    for(let dy=-search;dy<=search;dy+=step)for(let dx=-search;dx<=search;dx+=step){
      const x=expectedX+dx,y=expectedY+dy;
      const ring=ringDarknessImage(img,w,h,x,y,r,16);
      const outside=ringDarknessImage(img,w,h,x,y,r*1.38,12);
      const center=centerDarknessImage(img,w,h,x,y,Math.max(1,r*.20));
      const distancePenalty=Math.hypot(dx,dy)/Math.max(1,search)*.12;
      const score=ring*.82 + center*.10 - outside*.30 - distancePenalty;
      if(!best||score>best.score)best={x,y,r,score,ring,outside,offset:Math.hypot(dx,dy)};
    }
  }
  return best||{x:expectedX,y:expectedY,r:expectedR,score:0,ring:0,outside:0,offset:0};
}
function bubbleInteriorScoreFast(img,w,h,b){
  // v15.8.22 — score por CONTRASTE LOCAL.
  // Mede apenas o centro útil da bolha e compara cada pixel com o papel ao redor.
  // Isso neutraliza sombra, exposição e diferenças entre regiões da folha.
  const r=Math.max(3,b.r);
  const sampleR=r*.38; // longe da borda impressa, mesmo com pequeno erro de centro

  // Fundo local fora da circunferência impressa.
  let bgSum=0,bgSq=0,bgN=0;
  const outerR=r*1.52;
  const minX=Math.max(0,Math.floor(b.x-outerR)),maxX=Math.min(w-1,Math.ceil(b.x+outerR));
  const minY=Math.max(0,Math.floor(b.y-outerR)),maxY=Math.min(h-1,Math.ceil(b.y+outerR));
  for(let y=minY;y<=maxY;y++)for(let x=minX;x<=maxX;x++){
    const rr=Math.hypot(x-b.x,y-b.y);
    if(rr<r*1.22||rr>r*1.50)continue;
    const d=darknessAtImage(img,w,h,x,y);bgSum+=d;bgSq+=d*d;bgN++;
  }
  const bg=bgN?bgSum/bgN:0;
  const variance=bgN?Math.max(0,bgSq/bgN-bg*bg):0;
  const bgStd=Math.sqrt(variance);
  // Ruído do papel/câmera aumenta o limiar automaticamente, sem apagar marcas leves.
  const mediumDelta=Math.max(.070,bgStd*1.55);
  const strongDelta=Math.max(.150,bgStd*2.55);

  let excessSum=0,n=0,medium=0,strong=0,veryStrong=0;
  const cMinX=Math.max(0,Math.floor(b.x-sampleR)),cMaxX=Math.min(w-1,Math.ceil(b.x+sampleR));
  const cMinY=Math.max(0,Math.floor(b.y-sampleR)),cMaxY=Math.min(h-1,Math.ceil(b.y+sampleR));
  for(let y=cMinY;y<=cMaxY;y++)for(let x=cMinX;x<=cMaxX;x++){
    if(Math.hypot(x-b.x,y-b.y)>sampleR)continue;
    const dark=darknessAtImage(img,w,h,x,y);
    const delta=Math.max(0,dark-bg);
    excessSum+=delta;n++;
    if(delta>=mediumDelta)medium++;
    if(delta>=strongDelta)strong++;
    if(delta>=Math.max(.25,bgStd*3.2))veryStrong++;
  }
  if(!n)return 0;
  const meanExcess=excessSum/n;
  const mediumCoverage=medium/n,strongCoverage=strong/n,veryStrongCoverage=veryStrong/n;

  // Combinação robusta: preenchimento favorece média/cobertura; X e risco favorecem
  // pixels fortes. Em branco fica próximo de zero porque o fundo local é subtraído.
  const score=Math.max(
    meanExcess*1.75,
    mediumCoverage*.62 + meanExcess*.70,
    strongCoverage*.88 + meanExcess*.48,
    veryStrongCoverage*.98 + meanExcess*.38
  );
  return Math.max(0,Math.min(1,score));
}
function makeOmrDebugCanvas(canvas,details){
  if(typeof document==='undefined'||!document.createElement)return null;
  const out=document.createElement('canvas');out.width=canvas.width;out.height=canvas.height;
  const ctx=out.getContext('2d');ctx.drawImage(canvas,0,0);
  const scale=Math.max(1,Math.min(canvas.width,canvas.height)/700);
  ctx.lineWidth=2.2*scale;ctx.font=`${Math.round(11*scale)}px Arial`;ctx.textBaseline='bottom';
  for(const d of details){
    for(let i=0;i<(d.bubbles||[]).length;i++){
      const b=d.bubbles[i],letter=['A','B','C','D','E'][i]||String(i+1);
      const isSelected=d.selected===letter;
      ctx.strokeStyle=isSelected?'#17a34a':'#2474d8';
      ctx.beginPath();ctx.arc(b.x,b.y,b.r*.72,0,Math.PI*2);ctx.stroke();
      const val=Number(d.scores?.[i]||0);
      ctx.fillStyle=isSelected?'#17a34a':'#2474d8';
      ctx.fillText(`${letter}:${val.toFixed(2)}`,b.x-b.r*.7,b.y-b.r*.82);
    }
  }
  return out;
}

function bilerp(c,u,v){
  const top={x:c.tl.x+(c.tr.x-c.tl.x)*u,y:c.tl.y+(c.tr.y-c.tl.y)*u};
  const bot={x:c.bl.x+(c.br.x-c.bl.x)*u,y:c.bl.y+(c.br.y-c.bl.y)*u};
  return {x:top.x+(bot.x-top.x)*v,y:top.y+(bot.y-top.y)*v};
}

// Geometria EXATA do bloco de respostas impresso:
// response-frame 184x105mm; centros dos marcadores 179x100mm;
// bolhas alinhadas em 4 blocos, linhas de 9mm.
const OPTION_U=[
  [.09427,.13338,.17249,.21159],
  [.33869,.37779,.41690,.45601],
  [.58310,.62221,.66131,.70042],
  [.82751,.86662,.90573,.94483]
];
// v15.8.15 — geometria recalibrada pela folha REAL impressa.
// response-frame: 184 x 105 mm (border-box); marcadores de 5 mm em top/bottom 4 mm.
// Centros dos marcadores: y=6.5 mm e y=98.5 mm => vão vertical útil = 92 mm.
// A primeira bolha fica em y=28.5 mm (17 mm padding + 7 mm cabeçalho + 4.5 mm meia linha).
// Portanto: FIRST_V=(28.5-6.5)/92 = 0.2391304; ROW_STEP=9/92 = 0.0978261.
const FIRST_V=.2391304;
const ROW_STEP=.0978261;
export const OMR_LAYOUT={optionU:OPTION_U,firstV:FIRST_V,rowStep:ROW_STEP,markerAspect:1.79};

export function analyzeBubbleSheet(inputCanvas,itemCount,optionCount=4,options={}){
  // v15.8.22: quando a orientação já foi confirmada pelo QR da folha,
  // NÃO executamos a heurística geométrica novamente (0/180 são indistinguíveis por um retângulo simétrico).
  const normalized=options?.orientationLocked
    ? {canvas:inputCanvas,rotation:0,frame:detectAnswerFrame(inputCanvas)}
    : normalizeAnswerOrientation(inputCanvas);
  const canvas=normalized.canvas;
  const ctx=canvas.getContext('2d',{willReadFrequently:true});
  const frame=normalized.frame?.ok?normalized.frame:detectAnswerFrame(canvas);
  const corners=frame.corners;
  if(!corners) return {ok:false,error:'Não foi possível localizar os quatro marcadores do quadro do gabarito.',rotation:normalized.rotation};
  if(frame.coverage<.045) return {ok:false,error:'O gabarito está muito pequeno na imagem. Aproxime a câmera até o quadro de respostas ocupar quase toda a tela.',rotation:normalized.rotation};
  const cols=4,perCol=Math.ceil(itemCount/cols),answers=[],details=[];
  const frameHeight=frame.frameHeight||dist(corners.tl,corners.bl);
  const frameWidth=frame.frameWidth||dist(corners.tl,corners.tr);
  // Diâmetro impresso = 4.2 mm. Os centros dos marcadores abrangem 179 x 92 mm.
  // Usamos a média das escalas horizontal/vertical para estimar o raio físico na foto.
  const pxPerMm=((frameWidth/179)+(frameHeight/92))/2;
  const radius=Math.max(5,pxPerMm*2.1);
  const fullImage=ctx.getImageData(0,0,canvas.width,canvas.height).data;
  for(let q=0;q<itemCount;q++){
    const col=Math.min(cols-1,Math.floor(q/perCol)),row=q-col*perCol,v=FIRST_V+row*ROW_STEP;
    const bubbles=OPTION_U[col].slice(0,optionCount).map(u=>{
      const expected=bilerp(corners,u,v);
      const located=locatePrintedBubbleFast(fullImage,canvas.width,canvas.height,expected.x,expected.y,radius);
      return {...located,expectedX:expected.x,expectedY:expected.y};
    });
    const scores=bubbles.map(b=>bubbleInteriorScoreFast(fullImage,canvas.width,canvas.height,b));
    const result=classifyBubbleScores(scores);
    answers.push(result.selected);
    details.push({question:q+1,...result,bubbles});
  }
  const recognized=details.filter(x=>x.status==='recognized'||x.status==='low_confidence').length;
  const pending=details.filter(x=>x.status!=='recognized').length;
  const blanks=details.filter(x=>x.status==='blank').length;
  const duplicates=details.filter(x=>x.status==='duplicate').length;
  const lowConfidence=details.filter(x=>x.status==='low_confidence').length;
  const avgConfidence=details.reduce((s,x)=>s+x.confidence,0)/Math.max(1,details.length);
  return {
    ok:true,answers,details,recognized,total:itemCount,pending,blanks,duplicates,lowConfidence,
    confidence:avgConfidence,corners,optionCount,rotation:normalized.rotation,
    orientationLocked:!!options?.orientationLocked, orientationSource:options?.orientationSource||'',
    normalizedCanvas:canvas,frameQuality:frame.quality,frameCoverage:frame.coverage,
    debugCanvas:makeOmrDebugCanvas(canvas,details)
  };
}
